import React, { useState } from "react";
import styled from "styled-components";
import { DialogActions, DialogContent, DialogTitle, MenuItem } from "@material-ui/core";

import { IWorkspacePermissionAccess } from "common/interfaces";
import { Button as BeautifulButton } from "js/Components/Button";
import { Dropdown } from "js/Components/Dropdown";
import WorkspaceController from "js/controllers/WorkspaceController";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import { FolderAccessList } from "js/react/components/FolderAccessList";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";
import { getFullUserDisplayName } from "js/react/views/UserOptions/panes/ManageUsersPane/Components/UsersList/UserListCommon";

const StyledBeautifulDialog = styled(BeautifulDialog)`
    &&& {
        .MuiDialog-paper {
            padding: 40px 50px;
            gap: 20px;
            margin: 0;
        }
    }
`;

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StyledDialogTitle = styled.div`
    &&& {
        color: #333;
        font-family: "Source Sans Pro";
        font-size: 23px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 20px;
        overflow: visible;
    }
`;

const Subtitle = styled.div`
    color: #666;
    /* popup/body */
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%; /* 20px */
    letter-spacing: -0.32px;
`;

const FolderMembersAccessRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const FolderMembersAccessLabel = styled.span`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        gap: 20px;
        padding: 0;
        margin: 0;
    }
`;

const ButtonStyles = `
    margin: 0;
    padding: 6px 16px;
`;

const CancelButton = styled(BeautifulButton)`
    &&& {
        ${ButtonStyles}
        color: #666;
    }
`;

const ConfirmButton = styled(BeautifulButton)`
    &&& {
        ${ButtonStyles}
    }
`;

const FOLDER_MEMBERS_ACCESS_LABEL_ID = "folder-members-access-label";

type FolderMembersAccess = "edit" | "view";

/**
 * Resolves with the permissions the folder members get to the presentation(s), or with a falsy value when dismissed
 */
type CloseSharedFolderPresentationDialog = (folderMemberPermissions?: Omit<IWorkspacePermissionAccess, "owner"> | false) => void;

interface SharedFolderPresentationDialogProps {
    folderId: string;
    title: string;
    message: string;
    okButtonLabel: string;
    closeDialog: CloseSharedFolderPresentationDialog;
}

function SharedFolderPresentationDialog({
    folderId,
    title,
    message,
    okButtonLabel,
    closeDialog,
}: SharedFolderPresentationDialogProps) {
    const [folderMembersAccess, setFolderMembersAccess] = useState<FolderMembersAccess>("edit");

    const members = WorkspaceController.getUsersWithAccessToResourceFolder(folderId)
        .slice()
        .sort((a, b) =>
            getFullUserDisplayName(a.displayName, a.email)
                .localeCompare(getFullUserDisplayName(b.displayName, b.email))
        );

    const handleConfirm = () => {
        const canEdit = folderMembersAccess === "edit";
        closeDialog({
            read: { object: true, permissions: true },
            write: { object: canEdit, permissions: canEdit },
        });
    };

    return (
        <StyledBeautifulDialog closeDialog={() => closeDialog(false)} maxWidth="sm">
            <TitleSection>
                <StyledDialogTitle>
                    {title}
                </StyledDialogTitle>
                <Subtitle>
                    {message}
                </Subtitle>
            </TitleSection>
            <StyledDialogContent>
                <FolderMembersAccessRow>
                    <FolderMembersAccessLabel id={FOLDER_MEMBERS_ACCESS_LABEL_ID}>
                        {t("Folder members can")}
                    </FolderMembersAccessLabel>
                    <Dropdown
                        value={folderMembersAccess}
                        onChange={setFolderMembersAccess}
                        labelId={FOLDER_MEMBERS_ACCESS_LABEL_ID}
                        data-test-id="folder-members-access-dropdown"
                    >
                        <MenuItem value="edit">{t("Edit")}</MenuItem>
                        <MenuItem value="view">{t("View")}</MenuItem>
                    </Dropdown>
                </FolderMembersAccessRow>
                <FolderAccessList members={members} />
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton
                    unframed
                    large
                    color={themeColors.mediumGray}
                    onClick={() => closeDialog(false)}
                >
                    {t("Cancel")}
                </CancelButton>
                <ConfirmButton
                    large
                    blue
                    onClick={handleConfirm}
                >
                    {okButtonLabel}
                </ConfirmButton>
            </StyledDialogActions>
        </StyledBeautifulDialog>
    );
}

export interface CreatePresentationInSharedFolderDialogProps {
    folderId: string;
    closeDialog: CloseSharedFolderPresentationDialog;
}

export function CreatePresentationInSharedFolderDialog({
    folderId,
    closeDialog,
}: CreatePresentationInSharedFolderDialogProps) {
    return (
        <SharedFolderPresentationDialog
            folderId={folderId}
            title={t("Create in this shared folder?")}
            message={t("Everyone with access to this folder will also have access to this presentation.")}
            okButtonLabel={t("Create Presentation")}
            closeDialog={closeDialog}
        />
    );
}

export interface AddPresentationsToSharedFolderDialogProps {
    folderId: string;
    presentationCount: number;
    closeDialog: CloseSharedFolderPresentationDialog;
}

export function AddPresentationsToSharedFolderDialog({
    folderId,
    presentationCount,
    closeDialog,
}: AddPresentationsToSharedFolderDialogProps) {
    return (
        <SharedFolderPresentationDialog
            folderId={folderId}
            title={presentationCount === 1
                ? t("Add presentation to shared folder?")
                : t("Add {{count}} presentations to shared folder?", { count: presentationCount })}
            message={presentationCount === 1
                ? t("Everyone with access to this folder will also have access to this presentation.")
                : t("Everyone with access to this folder will also have access to these presentations.")}
            okButtonLabel={t("Add to folder")}
            closeDialog={closeDialog}
        />
    );
}
